import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { useLive } from '../../live.jsx';
import { Alert } from '../../components/ui.jsx';
import { openPrivateFile } from '../../privateFile.js';
import { fmtDate } from './ws.jsx';

// WhatsApp, Part 2 (migration 043): the conversation with this customer
// through the WhatsApp Business API — text, images, PDFs, a document from
// Documents (LOI, quotation…). Free messages only within 24 h of the
// customer's last message; otherwise an approved template.
const STATUS_ICON = { sent: '✓', delivered: '✓✓', read: '✓✓', failed: '⚠', received: '' };

export function MessageBubble({ m, lang, t }) {
  return (
    <div className={`wa-msg ${m.direction}${m.status === 'failed' ? ' failed' : ''}`}>
      {m.kind === 'template' && <span className="wa-tpl">{t('wa.template')}</span>}
      {m.media && (m.media.mime?.startsWith('image/')
        ? <button type="button" className="btn btn-link btn-sm" onClick={() => openPrivateFile(m.media.url.replace(/^\/api/, '')).catch(() => {})}>🖼 {m.media.name}</button>
        : <button type="button" className="btn btn-link btn-sm" onClick={() => openPrivateFile(m.media.url.replace(/^\/api/, '')).catch(() => {})}>📄 {m.media.name}</button>)}
      {m.body && <span>{m.body}</span>}
      <span className="wa-meta">{m.by ? `${m.by} · ` : ''}{fmtDate(m.at, lang, true)} <span style={m.status === 'read' ? { color: '#53bdeb' } : undefined}>{STATUS_ICON[m.status] || ''}</span></span>
      {m.status === 'failed' && <span className="field-error small">{m.error || t('wa.failed')}</span>}
    </div>
  );
}

export default function CustomerWhatsApp({ crmId }) {
  const { t, lang, errorText } = useI18n();
  const [d, setD] = useState(null);
  const [text, setText] = useState('');
  const [tplId, setTplId] = useState('');
  const [vars, setVars] = useState([]);
  const [docId, setDocId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const end = useRef(null);
  const load = useCallback(() => api(`/workspace/whatsapp/customers/${crmId}`).then(setD).catch((e) => setError(errorText(e))), [crmId, errorText]);
  useEffect(() => { load(); }, [load]);
  useLive(['whatsapp'], () => { load(); });
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [d?.messages?.length]);

  async function run(fn) {
    setBusy(true); setError('');
    try { await fn(); await load(); } catch (e) { setError(errorText(e)); await load(); } finally { setBusy(false); }
  }
  const sendText = () => run(async () => { await api(`/workspace/whatsapp/customers/${crmId}/text`, { method: 'POST', body: { text } }); setText(''); });
  const sendFile = (file) => run(async () => { const fd = new FormData(); fd.append('file', file); await api(`/workspace/whatsapp/customers/${crmId}/file`, { method: 'POST', form: fd }); });
  const sendDoc = () => run(async () => { await api(`/workspace/whatsapp/customers/${crmId}/document`, { method: 'POST', body: { documentId: Number(docId) } }); setDocId(''); });
  const sendTpl = () => run(async () => { await api(`/workspace/whatsapp/customers/${crmId}/template`, { method: 'POST', body: { templateId: Number(tplId), vars } }); setTplId(''); setVars([]); });
  const optin = (on) => run(() => api(`/workspace/whatsapp/customers/${crmId}/optin`, { method: 'POST', body: { optin: on } }));

  if (!d) return error ? <Alert>{error}</Alert> : <p className="muted">{t('common.loading')}</p>;
  if (!d.number) return <p className="callout callout-warning">{t('wa.noNumber')}</p>;
  const tpl = d.templates.find((x) => String(x.id) === String(tplId));
  const open = d.window.open;

  return (
    <div>
      <p className="small muted" style={{ marginTop: 0 }}>+{d.number}</p>
      <div className={`wa-window ${open ? 'open' : 'closed'}`} role="status">
        {open ? t('wa.windowOpen', { time: fmtDate(d.window.closesAt, lang, true) }) : t('wa.windowClosed')}
      </div>
      <div className="wa-thread" aria-live="polite">
        {d.messages.map((m) => <MessageBubble key={m.id} m={m} lang={lang} t={t} />)}
        {!d.messages.length && <p className="muted small" style={{ margin: 0 }}>{t('wa.noMessages')}</p>}
        <span ref={end} />
      </div>
      <Alert>{error}</Alert>
      {open && (
        <div className="wa-compose">
          <label className="sr-only" htmlFor="wa-text">{t('wa.message')}</label>
          <textarea id="wa-text" value={text} maxLength={4096} placeholder={t('wa.messagePh')} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && text.trim()) sendText(); }} />
          <div className="ws-row">
            <button type="button" className="btn btn-wa btn-sm" disabled={busy || !text.trim()} onClick={sendText}>{t('wa.send')}</button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => fileRef.current?.click()}>📎 {t('wa.file')}</button>
            <input ref={fileRef} type="file" className="sr-only" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => { if (e.target.files[0]) sendFile(e.target.files[0]); e.target.value = ''; }} />
            {d.documents.length > 0 && (
              <>
                <select aria-label={t('wa.document')} value={docId} onChange={(e) => setDocId(e.target.value)} style={{ width: 'auto', maxWidth: 260 }}>
                  <option value="">📄 {t('wa.document')}</option>
                  {d.documents.map((x) => <option key={x.id} value={x.id}>{x.ref} — {x.title}</option>)}
                </select>
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy || !docId} onClick={sendDoc}>{t('wa.sendDocument')}</button>
              </>
            )}
          </div>
        </div>
      )}
      <div className="wa-compose">
        <p className="field-label" style={{ margin: 0 }}>{t('wa.templateTitle')} <span className="field-optional">— {t('wa.templateHint')}</span></p>
        {d.optedOut && <p className="callout callout-warning small">{t('wa.optedOut')}</p>}
        <select aria-label={t('wa.templateTitle')} value={tplId} onChange={(e) => { setTplId(e.target.value); const x = d.templates.find((y) => String(y.id) === e.target.value); setVars(x ? x.variables.map(() => '') : []); }}>
          <option value="">{t('wa.chooseTemplate')}</option>
          {d.templates.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.lang.toUpperCase()})</option>)}
        </select>
        {tpl && (
          <>
            <p className="small muted" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tpl.body.replace(/\{\{(\d+)\}\}/g, (m, i) => vars[Number(i) - 1] || m)}</p>
            {tpl.variables.map((v, i) => <input key={i} aria-label={v} placeholder={`{{${i + 1}}} ${v}`} value={vars[i] || ''} onChange={(e) => setVars((a) => a.map((x, j) => (j === i ? e.target.value : x)))} />)}
            <div><button type="button" className="btn btn-wa btn-sm" disabled={busy || d.optedOut || vars.some((x) => !x.trim())} onClick={sendTpl}>{t('wa.sendTemplate')}</button></div>
          </>
        )}
        {!d.templates.length && <p className="small muted" style={{ margin: 0 }}>{t('wa.noTemplates')}</p>}
      </div>
      <label className="checkbox" style={{ marginTop: 12 }}>
        <input type="checkbox" checked={d.optin} disabled={busy} onChange={(e) => optin(e.target.checked)} />
        <span>{t('wa.customerOptin')}</span>
      </label>
    </div>
  );
}
